// src/components/TableViewWithStore.tsx
import React, { useState } from 'react';
import { Gathering, GatheringStatus } from '../../../../types';
import useAllGatherings from '../../hooks/data/useAllGatherings';
import { FilterState } from '../../hooks/useFilters';

interface TableViewProps {
  token: string | null;
  onViewDetails?: (gatheringId: string) => void;
  filters: FilterState;
}

const TableView: React.FC<TableViewProps> = ({ 
  token,
  onViewDetails,
  filters
}) => {
  const { gatherings: allGatherings, isLoading, isBackgroundLoading, error, refresh } = useAllGatherings(token);
  const [sortField, setSortField] = useState<keyof Gathering>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Apply filters to gatherings
  const filteredGatherings = allGatherings.filter(gathering => {
    // Apply space filter
    if (filters.spaceId && gathering.space?.id !== filters.spaceId) {
      return false;
    }

    // Apply date range filter
    if (filters.fromDate || filters.toDate) {
      const gatheringDate = new Date(gathering.date);
      
      if (filters.fromDate && gatheringDate < filters.fromDate) {
        return false;
      }
      
      if (filters.toDate) {
        // Set time to end of day for toDate comparison
        const endOfDay = new Date(filters.toDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        if (gatheringDate > endOfDay) {
          return false;
        }
      }
    }

    // Apply search filter
    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const query = filters.searchQuery.toLowerCase().trim();
      
      // Search in gathering title
      const titleMatch = gathering.title?.toLowerCase().includes(query);
      
      // Search in owner names
      let ownerMatch = false;
      if (gathering.owner) {
        const firstName = gathering.owner.first_name?.toLowerCase() || '';
        const lastName = gathering.owner.last_name?.toLowerCase() || '';
        const displayName = gathering.owner.display_name?.toLowerCase() || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        ownerMatch = 
          firstName.includes(query) ||
          lastName.includes(query) ||
          fullName.includes(query) ||
          displayName.includes(query);
      }
      
      // Return true if either title or owner matches
      if (!titleMatch && !ownerMatch) {
        return false;
      }
    }

    // Gathering passed all filters
    return true;
  });

  // Handle column header click for sorting
  const handleSort = (field: keyof Gathering) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort indicator for column headers
  const getSortIndicator = (field: keyof Gathering) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Format date and time for display
  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    // Parse the date string, ensuring we preserve the correct date
    // by handling the timezone offset properly
    const date = new Date(dateString);
    
    // Format date part - using shorter month format and no weekday to save space
    const dateOptions: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC' // Use UTC to prevent timezone shifts
    };
    const dateFormatted = date.toLocaleDateString('en-US', dateOptions);
    
    // Format time part
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC' // Use UTC to prevent timezone shifts
    };
    const timeFormatted = date.toLocaleTimeString('en-US', timeOptions);
    
    return (
      <div>
        <div className="date-primary">{dateFormatted}</div>
        <div className="date-time">{timeFormatted}</div>
      </div>
    );
  };

  // Sort gatherings based on current sort field and direction
  const sortedGatherings = [...filteredGatherings].sort((a, b) => {
    // Handle different field types appropriately
    if (sortField === 'date') {
      const dateA = new Date(a.date || '').getTime();
      const dateB = new Date(b.date || '').getTime();
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortField === 'title' || sortField === 'status') {
      const valueA = String(a[sortField] || '').toLowerCase();
      const valueB = String(b[sortField] || '').toLowerCase();
      return sortDirection === 'asc' 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA);
    } else if (sortField === 'id') {
      return sortDirection === 'asc' ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id);
    }
    
    // Default fallback
    return 0;
  });

  if (isLoading && allGatherings.length === 0) {
    return <div className="table-loading">Loading gatherings...</div>;
  }

  if (error && allGatherings.length === 0) {
    return (
      <div className="table-error">
        <p>{error}</p>
        <button onClick={refresh}>Retry</button>
      </div>
    );
  }

  if (filteredGatherings.length === 0) {
    return <div className="table-empty">No gatherings found</div>;
  }

  return (
    <div className="table-view">
      {isBackgroundLoading && (
        <div className="background-loading-indicator">
          <div className="spinner-small"></div>
          <span>Refreshing data...</span>
        </div>
      )}
      
      {error && (
        <div className="background-error-indicator">
          <p>{error}</p>
          <button onClick={refresh} className="small-button">Retry</button>
        </div>
      )}
      
      <table className="gatherings-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('title')} className="sortable-header">
              Title{getSortIndicator('title')}
            </th>
            <th onClick={() => handleSort('date')} className="sortable-header">
              Date & Time{getSortIndicator('date')}
            </th>
            <th onClick={() => handleSort('status')} className="sortable-header">
              Status{getSortIndicator('status')}
            </th>
            <th>Space</th>
            <th>Owner</th>
            <th onClick={() => handleSort('id')} className="sortable-header">
              ID{getSortIndicator('id')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedGatherings.map(gathering => (
            <tr 
              key={gathering.id} 
              onClick={() => onViewDetails && onViewDetails(gathering.id)}
              className="table-row"
            >
              <td className="gathering-title-cell">
                <div className="gathering-title-with-image">
                  {gathering.image_url && (
                    <div className="gathering-thumbnail">
                      <img src={gathering.image_url} alt="" />
                    </div>
                  )}
                  <div className="gathering-title-text">{gathering.title}</div>
                </div>
              </td>
              <td>{formatDateTime(gathering.date)}</td>
              <td>
                <span className={`status-badge status-${gathering.status.toLowerCase().replace(/\s+/g, '-')}`}>
                  {gathering.status}
                </span>
              </td>
              <td>{gathering.space?.name || 'N/A'}</td>
              <td>{gathering.owner?.display_name || 'N/A'}</td>
              <td className="gathering-id-cell">{gathering.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableView;